/**
 * Floating AI rewrite toolbar for Monaco editor.
 *
 * Shows preset actions + free-form input when text is selected.
 * Displays a preview of the AI result before applying.
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, Loader2, Check, X, SendHorizonal } from 'lucide-react';
import { promptRewriteApi } from '@/lib/api/promptRewrite';
import type * as Monaco from 'monaco-editor';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AIRewriteToolbarProps {
  editor: Monaco.editor.IStandaloneCodeEditor;
  /** Full editor value for context */
  fullText: string;
}

interface Position {
  top: number;
  left: number;
}

interface SelectionAnchor {
  /** bottom edge of selection (for showing toolbar below) */
  below: number;
  /** top edge of selection (for showing toolbar above) */
  above: number;
  left: number;
}

type Phase = 'idle' | 'input' | 'loading' | 'preview';

const PRESETS = [
  { label: '精简', instruction: '精简这段文字，去除冗余，保留核心意思' },
  { label: '扩展', instruction: '扩展这段文字，增加细节和深度，但保持主题一致' },
  { label: '润色', instruction: '润色这段文字，使语言更流畅专业' },
  { label: '翻译为英文', instruction: '翻译为英文' },
  { label: '翻译为中文', instruction: '翻译为中文' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AIRewriteToolbar: React.FC<AIRewriteToolbarProps> = ({ editor, fullText }) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [position, setPosition] = useState<Position | null>(null);
  const [anchor, setAnchor] = useState<SelectionAnchor | null>(null);
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState<Monaco.Selection | null>(null);
  const [customInput, setCustomInput] = useState('');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Drag state
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });
  // Debounce timer for showing toolbar
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Detect selection changes ----
  useEffect(() => {
    const disposable = editor.onDidChangeCursorSelection(() => {
      const selection = editor.getSelection();
      if (!selection || selection.isEmpty()) {
        // Cancel any pending show and hide immediately
        if (showTimerRef.current) { clearTimeout(showTimerRef.current); showTimerRef.current = null; }
        if (phase === 'idle' || phase === 'input') {
          setPhase('idle');
          setPosition(null);
        }
        return;
      }

      const model = editor.getModel();
      if (!model) return;

      const text = model.getValueInRange(selection);
      if (!text.trim()) {
        if (showTimerRef.current) { clearTimeout(showTimerRef.current); showTimerRef.current = null; }
        if (phase === 'idle' || phase === 'input') {
          setPhase('idle');
          setPosition(null);
        }
        return;
      }

      // Debounce: wait 300ms after the user stops changing the selection
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      showTimerRef.current = setTimeout(() => {
        showTimerRef.current = null;
        const latestSelection = editor.getSelection();
        if (!latestSelection || latestSelection.isEmpty()) return;
        const latestText = editor.getModel()?.getValueInRange(latestSelection) ?? '';
        if (!latestText.trim()) return;

        setSelectedText(latestText);
        setSelectionRange(latestSelection);

      // Compute anchor: above = top of selection end row, below = bottom of it
        const editorDom = editor.getDomNode();
        if (!editorDom) return;
        const editorRect = editorDom.getBoundingClientRect();

        // Try end position; if off-screen (e.g. Cmd+A on long text), fall back to last visible line
        const endPos = latestSelection.getEndPosition();
        let coords = editor.getScrolledVisiblePosition(endPos);
        if (!coords) {
          const visibleRanges = editor.getVisibleRanges();
          if (visibleRanges.length > 0) {
            const lastVisiblePos = visibleRanges[visibleRanges.length - 1].getEndPosition();
            coords = editor.getScrolledVisiblePosition(lastVisiblePos);
          }
        }
        if (!coords) return;

        const newAnchor: SelectionAnchor = {
          below: editorRect.top + coords.top + coords.height + 4,
          above: editorRect.top + coords.top - 4,
          left: Math.max(8, Math.min(editorRect.left + coords.left, window.innerWidth - 420)),
        };
        setAnchor(newAnchor);
        setPosition({ top: newAnchor.below, left: newAnchor.left });
        setPhase((prev) => {
          if (prev === 'idle') {
            setError('');
            setResult('');
            setCustomInput('');
            return 'input';
          }
          return prev;
        });
      }, 300);
    });

    return () => {
      disposable.dispose();
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
    };
  }, [editor, phase]);

  // After anchor or phase changes, flip toolbar above selection if it would overflow.
  // Must NOT depend on `position` to avoid infinite loop (setPosition → re-render → re-check).
  useLayoutEffect(() => {
    if (!toolbarRef.current || !anchor || phase === 'idle') return;
    if (isDraggingRef.current) return;
    const h = toolbarRef.current.offsetHeight;
    if (anchor.below + h > window.innerHeight - 8) {
      setPosition({ top: Math.max(8, anchor.above - h), left: anchor.left });
    } else {
      setPosition({ top: anchor.below, left: anchor.left });
    }
   
  }, [anchor, phase]);

  // Drag handlers
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      posX: position?.left ?? 0,
      posY: position?.top ?? 0,
    };

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - dragStartRef.current.mouseX;
      const dy = ev.clientY - dragStartRef.current.mouseY;
      setPosition({
        left: Math.max(0, Math.min(dragStartRef.current.posX + dx, window.innerWidth - 420)),
        top: Math.max(0, Math.min(dragStartRef.current.posY + dy, window.innerHeight - 60)),
      });
    };
    const onUp = () => {
      isDraggingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [position]);

  // NOTE: Do NOT auto-focus the input when toolbar appears.
  // Auto-focusing steals focus from Monaco and breaks Cmd+C (copy) for the selected text.

  // ---- Actions ----
  const handleClose = useCallback(() => {
    setPhase('idle');
    setPosition(null);
    setResult('');
    setError('');
    setCustomInput('');
    editor.focus();
  }, [editor]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase !== 'idle') {
        e.stopPropagation();
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [phase, handleClose]);

  const doRewrite = useCallback(async (instruction: string) => {
    if (!selectedText.trim() || !instruction.trim()) return;

    setPhase('loading');
    setError('');

    try {
      const resp = await promptRewriteApi.rewrite({
        selected_text: selectedText,
        instruction,
        context: fullText.length > 6000 ? undefined : fullText,
      });
      setResult(resp.rewritten_text);
      setPhase('preview');
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || '改写失败';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setPhase('input');
    }
  }, [selectedText, fullText]);

  const handlePresetClick = useCallback((instruction: string) => {
    doRewrite(instruction);
  }, [doRewrite]);

  const handleCustomSubmit = useCallback(() => {
    if (customInput.trim()) {
      doRewrite(customInput.trim());
    }
  }, [customInput, doRewrite]);

  const handleApply = useCallback(() => {
    if (!selectionRange || !result) return;
    const model = editor.getModel();
    if (model) {
      const currentText = model.getValueInRange(selectionRange);
      if (currentText !== selectedText) {
        setError('原文已更改，请重新选择文字');
        setPhase('input');
        return;
      }
    }
    editor.executeEdits('ai-rewrite', [
      { range: selectionRange, text: result },
    ]);
    handleClose();
  }, [editor, selectionRange, selectedText, result, handleClose]);

  // ---- Render ----
  if (!position || phase === 'idle') return null;

  return createPortal(
    <div
      ref={toolbarRef}
      className="fixed z-[100] animate-in fade-in slide-in-from-top-1 duration-150"
      style={{ top: position.top, left: position.left }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden max-w-[400px]">
        {/* Header — drag handle */}
        <div
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-violet-50 to-blue-50 border-b border-gray-100 cursor-grab active:cursor-grabbing select-none"
          onMouseDown={handleDragStart}
        >
          <Sparkles size={13} className="text-violet-500" />
          <span className="text-xs font-medium text-violet-700">AI 改写</span>
          <span className="text-[10px] text-gray-400 ml-1 truncate max-w-[160px]">
            {selectedText.length > 30 ? selectedText.slice(0, 30) + '...' : selectedText}
          </span>
          <span className="text-[10px] text-violet-400 shrink-0">· {selectedText.length}字</span>
          <button
            type="button"
            onClick={handleClose}
            onMouseDown={(e) => e.stopPropagation()}
            className="ml-auto p-0.5 text-gray-400 hover:text-gray-600 rounded"
          >
            <X size={13} />
          </button>
        </div>

        {/* Loading */}
        {phase === 'loading' && (
          <div className="flex items-center gap-2 px-3 py-3 text-xs text-gray-500">
            <Loader2 size={14} className="animate-spin text-violet-500" />
            AI 正在改写...
          </div>
        )}

        {/* Input phase: presets + custom input */}
        {phase === 'input' && (
          <div className="p-2">
            {error && (
              <div className="text-xs text-red-500 px-1 pb-1.5">{error}</div>
            )}
            {/* Presets */}
            <div className="flex flex-wrap gap-1 mb-2">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => handlePresetClick(p.instruction)}
                  className="px-2 py-0.5 text-xs rounded-full border border-gray-200 text-gray-600 hover:bg-violet-50 hover:border-violet-300 hover:text-violet-700 transition-colors"
                >
                  {p.label}
                </button>
              ))}
            </div>
            {/* Custom input */}
            <div className="flex items-center gap-1">
              <input
                ref={inputRef}
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    handleCustomSubmit();
                  }
                }}
                placeholder="输入自定义改写指令..."
                className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200"
              />
              <button
                type="button"
                onClick={handleCustomSubmit}
                disabled={!customInput.trim()}
                className="p-1 text-violet-500 hover:text-violet-700 disabled:text-gray-300 rounded"
              >
                <SendHorizonal size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Preview phase */}
        {phase === 'preview' && (
          <div className="p-2">
            <div className="text-[10px] text-gray-400 mb-1">改写结果：</div>
            <div className="max-h-[200px] overflow-auto px-2 py-1.5 bg-gray-50 rounded border border-gray-100 text-xs text-gray-800 whitespace-pre-wrap font-mono leading-relaxed">
              {result}
            </div>
            <div className="flex items-center justify-end gap-1.5 mt-2">
              <button
                type="button"
                onClick={() => { setPhase('input'); setResult(''); }}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 rounded border border-gray-200 hover:bg-gray-50"
              >
                重试
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 rounded border border-gray-200 hover:bg-gray-50"
              >
                <X size={12} />
                放弃
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="flex items-center gap-1 px-2.5 py-1 text-xs text-white bg-violet-600 hover:bg-violet-700 rounded font-medium"
              >
                <Check size={12} />
                应用
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default AIRewriteToolbar;
